import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X, Search, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useParentLineSearch,
  useAssignChildren,
  useRemoveChildren,
} from '@/hooks/usePIAB'
import { toast } from '@/stores/useToastStore'
import type { ParentLineItemInfo } from '@/types/dag'

export interface ChildToAssign {
  rowId: string
  skuNumber: string | null
  description?: string | null
  existingParent: ParentLineItemInfo | null
  unitQty?: unknown
}

interface AssignParentSkuDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  /** One entry for a single-line assignment, many for a bulk assignment. */
  childItems: ChildToAssign[]
  /** Called (in addition to onOpenChange(false)) after a successful save/remove. */
  onSuccess?: () => void
}

export function AssignParentSkuDrawer({
  open,
  onOpenChange,
  planId,
  versionId,
  childItems,
  onSuccess,
}: AssignParentSkuDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedParent, setSelectedParent] = useState<{
    id: string
    skuNumber: string | null
    description: string | null
  } | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)
  const [clearQuantity, setClearQuantity] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const isBulk = childItems.length > 1

  const { data: searchData, isLoading: isSearching } = useParentLineSearch(
    planId,
    versionId,
    searchQuery
  )

  const assignMutation = useAssignChildren(planId, versionId)
  const removeMutation = useRemoveChildren(planId, versionId)

  const parentRows = searchData?.rows ?? []

  // When every selected child already shares the same parent, treat it as
  // "the" existing parent (prefilled + eligible for removal). A bulk
  // selection with mixed current parents is left blank for the user to
  // resolve explicitly.
  const commonExistingParent = useMemo(() => {
    if (childItems.length === 0) return null
    const first = childItems[0].existingParent
    if (!first) return null
    return childItems.every(c => c.existingParent?.id === first.id)
      ? first
      : null
  }, [childItems])

  const hasAnyExistingParent = useMemo(
    () => childItems.some(c => c.existingParent),
    [childItems]
  )

  // Reset state and invalidate cached parent list when drawer opens
  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setSelectedParent(
        commonExistingParent
          ? {
              id: commonExistingParent.id,
              skuNumber: commonExistingParent.sku_number ?? null,
              description: commonExistingParent.description ?? null,
            }
          : null
      )
      setShowDropdown(false)
      setRemoveConfirmOpen(false)
      setClearQuantity(false)
      queryClient.removeQueries({
        queryKey: ['parent-line-search', planId, versionId],
      })
    }
  }, [open, queryClient, planId, versionId, commonExistingParent])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedParent(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    setShowDropdown(true)
  }

  const handleSelectParent = useCallback(
    (row: { id: string; cells: Record<string, unknown> }) => {
      setSelectedParent({
        id: row.id,
        skuNumber: (row.cells['plan_sku_num'] as string) ?? null,
        description: (row.cells['description'] as string) ?? null,
      })
      setSearchQuery(
        String(row.cells['plan_sku_num'] ?? row.cells['description'] ?? '')
      )
      setShowDropdown(false)
    },
    []
  )

  const handleSave = useCallback(async () => {
    if (!selectedParent) return

    setIsSubmitting(true)
    try {
      // Children currently linked to a different parent must be unlinked
      // there first, grouped by their current parent (a bulk selection can
      // span several different existing parents at once).
      const toReassign = childItems.filter(
        c => c.existingParent && c.existingParent.id !== selectedParent.id
      )

      if (toReassign.length > 0) {
        const byParent = new Map<string, string[]>()
        for (const child of toReassign) {
          const parentId = child.existingParent!.id
          const ids = byParent.get(parentId) ?? []
          ids.push(child.rowId)
          byParent.set(parentId, ids)
        }

        await Promise.all(
          Array.from(byParent.entries()).map(([parentId, childLineItemIds]) =>
            removeMutation.mutateAsync({
              lineId: parentId,
              childLineItemIds,
            })
          )
        )
      }

      await assignMutation.mutateAsync({
        lineId: selectedParent.id,
        childLineItemIds: childItems.map(c => c.rowId),
        keepPiabQuantities: !clearQuantity,
      })

      onOpenChange(false)
      onSuccess?.()
    } catch {
      // Errors are already surfaced via toast by the mutation hooks.
    } finally {
      setIsSubmitting(false)
    }
  }, [
    selectedParent,
    childItems,
    clearQuantity,
    removeMutation,
    assignMutation,
    onOpenChange,
    onSuccess,
  ])

  const handleRemoveParent = useCallback(async () => {
    const withParent = childItems.filter(c => c.existingParent)
    if (withParent.length === 0) return

    setIsSubmitting(true)
    try {
      const byParent = new Map<string, string[]>()
      for (const child of withParent) {
        const parentId = child.existingParent!.id
        const ids = byParent.get(parentId) ?? []
        ids.push(child.rowId)
        byParent.set(parentId, ids)
      }

      await Promise.all(
        Array.from(byParent.entries()).map(([parentId, childLineItemIds]) =>
          removeMutation.mutateAsync({ lineId: parentId, childLineItemIds })
        )
      )

      toast.success(
        isBulk
          ? 'Parent SKUs removed successfully'
          : 'Parent SKU removed successfully'
      )
      setRemoveConfirmOpen(false)
      onOpenChange(false)
      onSuccess?.()
    } catch {
      // Errors are already surfaced via toast by the mutation hooks.
    } finally {
      setIsSubmitting(false)
    }
  }, [childItems, removeMutation, isBulk, onOpenChange, onSuccess])

  const shouldShowDropdown = showDropdown
  const isSaving = isSubmitting
  const hasQuantity = childItems.some(
    c => c.unitQty !== null && c.unitQty !== undefined && c.unitQty !== ''
  )

  return (
    <>
      <Sheet open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
        <SheetContent
          side="right"
          className="w-[480px] sm:max-w-[480px] flex flex-col"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              Assign Parent SKU
              {!isBulk && childItems[0]?.skuNumber && (
                <Badge variant="secondary">{childItems[0].skuNumber}</Badge>
              )}
              {isBulk && (
                <Badge variant="secondary">{childItems.length} SKUs</Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              {isBulk
                ? `Search and select a parent SKU to assign to the ${childItems.length} selected child line items.`
                : 'Search and select a parent SKU to assign to this child line item.'}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {isBulk && (
              <div className="rounded-md border border-border overflow-hidden">
                <div className="px-3 py-2 border-b bg-muted/50 text-xs font-medium text-muted-foreground uppercase">
                  Selected Child SKUs ({childItems.length})
                </div>
                <div className="max-h-[160px] overflow-y-auto divide-y">
                  {childItems.map(child => (
                    <div
                      key={child.rowId}
                      className="px-3 py-2 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          SKU #{child.skuNumber || 'No SKU'}
                        </p>
                        {child.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {child.description}
                          </p>
                        )}
                      </div>
                      {child.existingParent &&
                        child.existingParent.id !== selectedParent?.id && (
                          <Badge
                            variant="outline"
                            className="text-[10px] shrink-0"
                          >
                            Reassign
                          </Badge>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search bar — hidden when a parent is selected */}
            {!selectedParent && (
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    placeholder="Search for parent SKU..."
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    className="pl-9"
                  />
                </div>

                {/* Search results dropdown */}
                {shouldShowDropdown && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-lg max-h-[240px] overflow-auto">
                    {isSearching ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : parentRows.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        No parent SKUs found
                      </div>
                    ) : (
                      <div className="px-2 py-1.5">
                        <p className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                          Suggestions
                        </p>
                        {parentRows.map(row => {
                          const parentSku = String(
                            row.cells['plan_sku_num'] ?? ''
                          )
                          return (
                            <div
                              key={row.id}
                              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                              onClick={() => handleSelectParent(row)}
                            >
                              <p className="flex-1 text-sm font-medium text-muted-foreground truncate">
                                SKU #{parentSku || 'No SKU'}
                              </p>
                              <p className="text-xs font-medium text-muted-foreground truncate">
                                {String(row.cells['description'] ?? '')}
                              </p>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected parent card */}
            {selectedParent && (
              <div className="rounded-md border border-border p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">
                    SKU #{selectedParent.skuNumber || 'No SKU'}
                  </p>
                  {selectedParent.description && (
                    <p className="text-xs text-muted-foreground">
                      {selectedParent.description}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 cursor-pointer"
                  onClick={() => {
                    setSelectedParent(null)
                    setSearchQuery('')
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {hasQuantity && (
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="clear-qty"
                    checked={clearQuantity}
                    onCheckedChange={checked =>
                      setClearQuantity(checked === true)
                    }
                  />
                  <label
                    htmlFor="clear-qty"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Clear existing PIAB Quantity
                  </label>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Leave unchecked to keep the current Unit Qty per PIAB after
                  assigning the parent.
                </p>
              </div>
            )}
          </div>

          <SheetFooter className="flex-row flex-shrink-0 items-center justify-end gap-2.5 border-t border-[#D6DAE1] px-6 py-6">
            {hasAnyExistingParent && (
              <button
                type="button"
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={isSaving}
                className="mr-auto text-sm text-destructive hover:text-destructive/80 underline cursor-pointer disabled:opacity-50"
              >
                Remove Parent
              </button>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!selectedParent || isSaving}
              className="bg-[#CF1F2E] hover:bg-[#B01A26] text-white cursor-pointer"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save Parent SKU'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        title="Remove Parent SKU"
        description={
          isBulk
            ? 'Are you sure you want to remove the parent SKU assignment from the selected child line items? This will unlink them from their parent(s).'
            : 'Are you sure you want to remove the parent SKU assignment? This will unlink this child from its parent.'
        }
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleRemoveParent}
        variant="destructive"
        isLoading={isSubmitting}
      />
    </>
  )
}
